import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Mail, User, Calendar, Eye, Edit, Trash2, CheckCircle, Clock, Phone, FileText } from 'lucide-react';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useToast } from './components/ui/Toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const RecommendationsCRUD = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, new, reviewed, contacted, completed

  // Initialize toast and escape key hook
  const { toast } = useToast();
  useEscapeKey(() => setShowModal(false), showModal);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-800 border-blue-200',
    reviewed: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    contacted: 'bg-purple-100 text-purple-800 border-purple-200',
    completed: 'bg-green-100 text-green-800 border-green-200'
  };

  const statusIcons = {
    new: <MessageSquare className="h-4 w-4" />,
    reviewed: <Eye className="h-4 w-4" />,
    contacted: <Phone className="h-4 w-4" />,
    completed: <CheckCircle className="h-4 w-4" />
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/recommendations`, {
        headers: getAuthHeaders()
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Error fetching recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateRecommendationStatus = async (id, status, adminNotes = null) => {
    setIsSubmitting(true);
    try {
      const payload = { status };
      if (adminNotes !== null) {
        payload.admin_notes = adminNotes;
      }

      await axios.put(`${API_BASE_URL}/api/admin/recommendations/${id}`, payload, {
        headers: getAuthHeaders()
      });
      await fetchRecommendations();
      setShowModal(false);
      setSelectedRecommendation(null);
      toast.success('Recommendation updated successfully!');
    } catch (error) {
      console.error('Error updating recommendation:', error);
      toast.error('Error updating recommendation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteRecommendation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recommendation?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/recommendations/${id}`, {
        headers: getAuthHeaders()
      });
      await fetchRecommendations();
      toast.success('Recommendation deleted successfully!');
    } catch (error) {
      console.error('Error deleting recommendation:', error);
      toast.error('Error deleting recommendation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModal = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedRecommendation(null);
    setShowModal(false);
  };

  const getFilteredRecommendations = () => {
    if (filter === 'all') return recommendations;
    return recommendations.filter(rec => rec.status === filter);
  };

  const getStatusStats = () => {
    const stats = {
      all: recommendations.length,
      new: recommendations.filter(r => r.status === 'new').length,
      reviewed: recommendations.filter(r => r.status === 'reviewed').length,
      contacted: recommendations.filter(r => r.status === 'contacted').length,
      completed: recommendations.filter(r => r.status === 'completed').length,
    };
    return stats;
  };

  const stats = getStatusStats();
  const filteredRecommendations = getFilteredRecommendations();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Agent Recommendations</h2>
          <p className="text-gray-600">Manage recommendations from website visitors</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Recommendations</div>
          <div className="text-2xl font-bold text-primary-600">{stats.all}</div>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: stats.all },
            { key: 'new', label: 'New', count: stats.new },
            { key: 'reviewed', label: 'Reviewed', count: stats.reviewed },
            { key: 'contacted', label: 'Contacted', count: stats.contacted },
            { key: 'completed', label: 'Completed', count: stats.completed }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Recommendations Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading recommendations...</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requirements
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecommendations.map((recommendation) => (
                <tr key={recommendation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{recommendation.name}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {recommendation.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs">
                      <p className="truncate">{recommendation.requirements}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[recommendation.status]}`}>
                      {statusIcons[recommendation.status]}
                      <span className="ml-1 capitalize">{recommendation.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(recommendation.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openModal(recommendation)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => deleteRecommendation(recommendation.id)}
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {filteredRecommendations.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No recommendations found for the selected filter.</p>
          </div>
        )}
      </div>

      {/* Detailed View Modal */}
      {showModal && selectedRecommendation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Recommendation Details
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-sm text-gray-900">{selectedRecommendation.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="text-sm text-gray-900">{selectedRecommendation.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Submitted</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRecommendation.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRecommendation.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Operational Requirements
                </h4>
                <div className="bg-white border rounded-lg p-4">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {selectedRecommendation.requirements}
                  </p>
                </div>
              </div>

              {/* Status Management */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Edit className="h-5 w-5 mr-2" />
                  Status Management
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Status
                    </label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColors[selectedRecommendation.status]}`}>
                      {statusIcons[selectedRecommendation.status]}
                      <span className="ml-2 capitalize">{selectedRecommendation.status}</span>
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['new', 'reviewed', 'contacted', 'completed'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateRecommendationStatus(selectedRecommendation.id, status)}
                        disabled={isSubmitting}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 ${
                          selectedRecommendation.status === status
                            ? statusColors[status]
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {isSubmitting ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                        ) : (
                          statusIcons[status]
                        )}
                        <span className="ml-1 capitalize">{status}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Admin Notes</h4>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={4}
                  placeholder="Add notes about this recommendation..."
                  defaultValue={selectedRecommendation.admin_notes || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (selectedRecommendation.admin_notes || '')) {
                      updateRecommendationStatus(selectedRecommendation.id, selectedRecommendation.status, e.target.value);
                    }
                  }}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <a
                  href={`mailto:${selectedRecommendation.email}?subject=Re: Your AI Agent Recommendation&body=Hi ${selectedRecommendation.name},%0D%0A%0D%0AThank you for your AI agent recommendation. We've reviewed your requirements:%0D%0A%0D%0A"${selectedRecommendation.requirements}"%0D%0A%0D%0ABest regards,%0D%0AUltralender.ai Team`}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationsCRUD;